import _ from 'lodash';

import defaultMetricWeightService from '@/services/default-metric-weight-service';
import scoreCalculatorService from '@/services/score-calculator-service';
import socialnetworkCalculatorService from '@/services/socialnetwork-calculator-service';

import { dateHelperService } from '@/services/date-helper-service';
import { cacheService } from '@/services/cache-service.js';

import constants from '@/constants.js';

class ScoreService {
    async calculateScores (project, rawMetrics, period, daySmoothing, firstDayOfWeek) {
        let metricWeights = project.metricWeights;
        let pillarWeights = project.pillarWeights;
        const hash = cacheService.getHash(project, period, daySmoothing, firstDayOfWeek);
        if (!cacheService.hasKey(hash)) {
            try {
                if (!metricWeights || metricWeights.length === 0) {
                    let metricWeightsResult = await defaultMetricWeightService.getDefaultMetricWeights();
                    metricWeights = metricWeightsResult.data;
                }
                if (!pillarWeights || pillarWeights.length === 0) {
                    let pillarWeightsResult = await defaultMetricWeightService.getDefaultPillarWeights();
                    pillarWeights = pillarWeightsResult.data;
                }

                // Use the sysMetric names that are set in the DefaultWeights DB table to match.
                let pillarWeightsObj = {
                    conversation: _.find(pillarWeights, p => p.sysMetric === 'conversation').weight,
                    quality: _.find(pillarWeights, p => p.sysMetric === 'quality').weight,
                    community: _.find(pillarWeights, p => p.sysMetric === 'community').weight
                };

                const columnNameDict = _.fromPairs(project.columns.map(el => [el.id, el.name]));

                // we need to apply period averaging here for all types.
                const metricFields = metricWeights.map(m => m.sysMetric);
                let metrics = averageMetrics(rawMetrics, metricFields, period, daySmoothing, firstDayOfWeek);

                let reducedMetrics = _.reduce(_.groupBy(metrics, 'metricDate'),
                    (acc, collection, date) => {
                        // find the min & max values within the collection for the current date (or trend period)
                        let min = {};
                        let max = {};
                        metricWeights.forEach(w => {
                            min[w.sysMetric] = _.min(collection.map(m => m[w.sysMetric]));
                            max[w.sysMetric] = _.max(collection.map(m => m[w.sysMetric]));
                        });

                        // calculate scores
                        const brandMetrics = collection;
                        const normedBrandMetrics = scoreCalculatorService.normaliseBrandMetrics(collection, metricWeights, min, max, false);
                        const normedWeightedBrandMetrics = scoreCalculatorService.normaliseBrandMetrics(collection, metricWeights, min, max, true);
                        const score = scoreCalculatorService.calculateFinalScores(normedWeightedBrandMetrics, pillarWeightsObj);
                        const socialnetworks = socialnetworkCalculatorService.calculateSocialNetworkScores(collection, metricWeights, pillarWeightsObj, min, max);

                        // merge final scores + socialnetworks
                        score.forEach(s => {
                            const networks = socialnetworks.find(el => el.columnId === s.columnId);
                            const metrics = brandMetrics.find(el => el.columnId === s.columnId);
                            const normedMetrics = normedBrandMetrics.find(el => el.columnId === s.columnId);
                            const normedWeightedMetrics = normedWeightedBrandMetrics.find(el => el.columnId === s.columnId);
                            s.day = date;
                            s.periodName = collection[0].periodName;
                            s.periodStartDate = dateHelperService.formatForDateAxis(collection[0].periodStartDate);
                            s.periodEndDate = dateHelperService.formatForDateAxis(collection[0].periodEndDate);
                            s.brandName = columnNameDict[s.columnId];
                            s.networks = {
                                nb: networks.nb,
                                fb: networks.fb,
                                tw: networks.tw,
                                yt: networks.yt,
                                ig: networks.ig
                            };
                            // for normalized
                            s.brandMetrics = _.pick(metrics, metricFields);
                            s.normedBrandMetrics = {
                                ...normedMetrics.xConversation,
                                ...normedMetrics.yQuality,
                                ...normedMetrics.zCommunity
                            };
                            s.normedWeightedBrandMetrics = {
                                ...normedWeightedMetrics.xConversation,
                                ...normedWeightedMetrics.yQuality,
                                ...normedWeightedMetrics.zCommunity
                            };
                        });
                        acc.push(...score);
                        return acc;
                    }, []);
                cacheService.setCache(hash, reducedMetrics);
            } catch (e) {
                return [];
            }
        }
        return cacheService.getCache(hash);
    }
};

const averageMetrics = (metrics, metricFields, period, daySmoothing, firstDayOfWeek) => {
    /*
        1. group by brand/colId
        2. group by date/period
        3. average brand metrics within the period
        4. rebuild other metric fields with averaged values
        5. rejoin all rebuilt metrics
    */
    let result = [];
    let metricsByBrand = _.groupBy(metrics, m => m.columnId);
    let columnIds = Object.keys(metricsByBrand);
    columnIds.forEach(columnId => {
        let brandMetrics = metricsByBrand[columnId];
        let brandMetricsByPeriod = groupMetricByPeriod(brandMetrics, period, firstDayOfWeek, daySmoothing);

        let periods = Object.keys(brandMetricsByPeriod);
        periods.forEach(p => {
            let brandMetricsInPeriod = brandMetricsByPeriod[p];
            let averageMetricsForPeriod = averageMetricValues(brandMetricsInPeriod, metricFields);
            averageMetricsForPeriod.name = brandMetricsInPeriod[0].name;
            averageMetricsForPeriod.dayDS = brandMetricsInPeriod[0].dayDS;
            averageMetricsForPeriod.metricDate = brandMetricsInPeriod[0].metricDate;
            averageMetricsForPeriod.periodStartDate = _.minBy(brandMetricsInPeriod, 'metricDate').metricDate;
            averageMetricsForPeriod.periodEndDate = _.maxBy(brandMetricsInPeriod, 'metricDate').metricDate;
            averageMetricsForPeriod.dayStamps = brandMetricsInPeriod.map(m => m.dayDS);
            averageMetricsForPeriod.columnId = columnId;
            // If period is week, set the period name to the date of the last day of that week (Period end date).
            averageMetricsForPeriod.periodName = period === constants.PERIOD.WEEK ? dateHelperService.formatForDateAxis(averageMetricsForPeriod.periodEndDate) : p;

            result = result.concat(averageMetricsForPeriod);
        });
    });

    return result;
};

const groupMetricByPeriod = (metrics, period, firstDayOfWeek, daySmoothing) => {
    // The metrics must be sorted by date before grouping it by periods,
    // as these metrics are added into each period in a sequential order.
    // If the metrics are not in date order, we may see some incorrect averaging results.
    let sortedMetrics = _.sortBy(metrics, 'dayDS');
    if (period === constants.PERIOD.DAY && daySmoothing && daySmoothing > 1) {
        let metricsGroupedBySmoothedDay = {};
        for (var i = daySmoothing - 1; i < sortedMetrics.length; i += 1) {
            let eachDaysMetrics = [];
            for (var iPlusDs = i; iPlusDs > i - daySmoothing; iPlusDs -= 1) {
                eachDaysMetrics = eachDaysMetrics.concat(sortedMetrics[iPlusDs]);
            }
            let date = dateHelperService.formatForDateAxis(sortedMetrics[i].metricDate);
            metricsGroupedBySmoothedDay[date] = eachDaysMetrics;
        }
        return metricsGroupedBySmoothedDay;
    }

    // default grouper for constants.PERIOD.DAY
    let fnDateGrouper = dateHelperService.formatForDateAxis;
    switch (period) {
    case constants.PERIOD.WEEK:
        fnDateGrouper = date => calculateWeek(date, firstDayOfWeek);
        break;
    case constants.PERIOD.MONTH:
        fnDateGrouper = dateHelperService.formatAsMonth;
        break;
    case constants.PERIOD.QUARTER:
        fnDateGrouper = dateHelperService.formatAsQuarter;
        break;
    case constants.PERIOD.YEAR:
        fnDateGrouper = dateHelperService.formatAsYear;
        break;
    }

    // note that the grouper needs to create a unique label for each date period (day/week/month etc.)
    return _.groupBy(sortedMetrics, s => fnDateGrouper(s.metricDate));
};

const calculateWeek = (date, firstDayOfWeek) => {
    // dates, ffs. This isn't entirely accurate, but will work until locales are implemented
    if (firstDayOfWeek === constants.DAY.SATURDAY) {
        return dateHelperService.formatAsWeek(date, -2);
    }
    if (firstDayOfWeek === constants.DAY.SUNDAY) {
        return dateHelperService.formatAsWeek(date, -1);
    }

    return dateHelperService.formatAsWeek(date);
};

const averageMetricValues = (metrics, metricFields) => {
    // only average the metric fields themselves
    let averagedMetric = {};
    metricFields.forEach(fieldName => {
        averagedMetric[fieldName] = _.meanBy(metrics, fieldName);
    });

    return averagedMetric;
};

export const scoreService = new ScoreService();
